import React, { useState } from "react";
import { Button } from "components/Graphs/Button";
import { Check, Code, Copy, FileCode, RotateCcw } from "lucide-react";
import FlatCard from "components/UI-lib/FlatCard";
import { Card, CardContent, CardHeader, Tooltip } from "@mui/material";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  CloudArrowUpIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  InformationCircleIcon,
  MagnifyingGlassCircleIcon,
} from "@heroicons/react/24/outline";
import { LaunchablePath } from "./launchable-intent-selector";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { Input } from "components/Graphs/Input";
import { AnimatePresence, motion } from "framer-motion";
import { cx } from "components/Graphs/utils";

type RuntimeType = "VM" | "container" | null;

interface ReviewConfigurationProps {
  path: LaunchablePath;
  runtime: RuntimeType;
  codeSource: string;
  container: ContainerSelected;
  jupyterStatus: "configured" | "not-configured";
  handleShowMockDeployModal: (show: boolean) => void;
  name: string;
  setName: (name: string) => void;
  launchableURL: string;
  loading: boolean;
  handleReset: () => void;
}

export const ReviewConfiguration: React.FC<ReviewConfigurationProps> = ({
  path,
  runtime,
  codeSource,
  jupyterStatus,
  handleShowMockDeployModal,
  name,
  setName,
  launchableURL,
  loading,
  handleReset,
}) => {
  const [copied, setCopied] = useState(false);

  // Derive status from props
  const status = loading ? "loading" : launchableURL ? "success" : "idle";

  const handleCopy = () => {
    navigator.clipboard.writeText(launchableURL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4 text-primary">
        Review Your Launchable
      </h2>
      <p className="text-secondary mb-6">
        Review your configuration above before creating your Launchable
      </p>

      <div className="space-y-6">
        <FlatCard className="bg-highlight border-highlight">
          <div className="pb-2">
            <h3 className="flex items-center font-semibold text-primary">
              <CloudArrowUpIcon className="mr-1 h-6 w-6 text-highlight" />
              Ready to Create
            </h3>
          </div>
          <div>
            <p className="text-sm text-secondary mb-4">
              Your Launchable configuration is ready. Enter a name and click
              "Create Launchable" to create your environment template.
            </p>
            <label htmlFor="name" className="text-sm text-primary">
              Name
            </label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a name for your Launchable"
              disabled={status === "loading" || status === "success"}
            />
            <div className="flex items-center text-xs text-secondary">
              <Code className="mr-1 h-4 w-4" />
              <span>
                The configuration will be saved as a link to easily share with
                others.
              </span>
            </div>
            <div className="flex w-full">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    initial={{ opacity: 0, height: 0, scale: 0.95 }}
                    animate={{ opacity: 1, height: "auto", scale: 1 }}
                    exit={{ opacity: 0, height: 0, scale: 0.95 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.22, 1, 0.36, 1],
                      scale: { duration: 0.4 },
                    }}
                    className="w-full bg-secondaryBg border border-highlight rounded-lg p-4 space-y-3 overflow-hidden mt-4"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                    >
                      <h4 className="font-medium text-highlight flex items-center gap-2">
                        <motion.span
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            delay: 0,
                          }}
                        >
                          <Check className="w-5 h-5" />
                        </motion.span>
                        <motion.span
                          initial={{ opacity: 0, x: -5 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3, duration: 0.3 }}
                        >
                          Launchable Created Successfully
                        </motion.span>
                      </h4>
                    </motion.div>

                    <motion.div
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      <Input
                        value={launchableURL}
                        readOnly
                        className="font-mono text-sm bg-background"
                      />
                      <Tooltip
                        title={copied ? "Copied" : "Copy to Clipboard"}
                        placement="top"
                      >
                        <div>
                          <Button
                            variant="secondary"
                            onClick={handleCopy}
                            className={cx(
                              "transition-all duration-300",
                              copied &&
                                "bg-highlight text-highlight border-highlight hover:bg-highlight hover:text-highlight hover:border-highlight"
                            )}
                          >
                            <AnimatePresence mode="wait">
                              {copied ? (
                                <motion.span
                                  key="check"
                                  initial={{ scale: 0.5, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0.5, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <ClipboardDocumentCheckIcon className="w-4 h-4" />
                                </motion.span>
                              ) : (
                                <motion.span
                                  key="copy"
                                  initial={{ scale: 0.5, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0.6, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <DocumentDuplicateIcon className="w-4 h-4" />
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </Button>
                        </div>
                      </Tooltip>
                    </motion.div>

                    <motion.div
                      className="flex items-center gap-2 mt-2"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <Button
                        variant="secondary"
                        className="gap-2"
                        onClick={() => window.open(launchableURL, "_blank")}
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        View Live Deploy Page
                      </Button>
                      <Button
                        variant="secondary"
                        className="gap-2"
                        onClick={handleReset}
                      >
                        <RotateCcw className="w-4 h-4" />
                        Create Another
                      </Button>
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0, scale: 0.95 }}
                    animate={{ opacity: 1, height: "auto", scale: 1 }}
                    exit={{ opacity: 0, height: 0, scale: 0.95 }}
                    transition={{
                      duration: 0.4,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="overflow-hidden"
                  >
                    <Button
                      variant="secondary"
                      className="gap-2 mt-2"
                      onClick={() => {
                        handleShowMockDeployModal(true);
                      }}
                    >
                      <EyeIcon className="w-4 h-4" />
                      Preview Deploy Page
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </FlatCard>
      </div>
    </div>
  );
};
